//Changes made here must be mirrored on backend
class LogLevel {
	static NONE = new LogLevel(0, 'NONE');
	static ERROR = new LogLevel(1, 'ERROR');
	static WARN = new LogLevel(2, 'WARN');
	static INFO = new LogLevel(3, 'INFO');
	static DEBUG = new LogLevel(4, 'DEBUG');

	id: number;
	enumKey: string;

	constructor(id, enumKey) {
		this.id = id;
		this.enumKey = enumKey;
	}

	static fromId(id) {
		switch (id) {
			case 0:
				return this.NONE;
			case 1:
				return this.ERROR;
			case 2:
				return this.WARN;
			case 3:
				return this.INFO;
			case 4:
				return this.DEBUG;
		}
		throw new Error(`Could not find LogLevel with id ${id}`);
	}
}

// localStorage is not defined during server side rendering
const localStorageInstance = typeof localStorage !== 'undefined' ? localStorage : null;

const savedLogLevel = localStorageInstance?.getItem('bfinforLogLevel');
const savedOverride = localStorageInstance?.getItem('bfinforLogLevelOverride');

/**
 * Logger class;
 * takes a name or a function/component as an argument and uses it as prefix
 *
 * example usage
 *
 * ...import Logger..
 *
 * const log = new Logger(MyComponent);
 * log.error('I have an', err);
 */
export default class Logger {
	static _rootLevel: LogLevel = LogLevel[savedLogLevel ? savedLogLevel : 'INFO'];

	// override default level for given logger name
	static _overrideLevel = savedOverride ? new Map(JSON.parse(savedOverride)) : new Map();

	static reset() {
		localStorageInstance?.setItem('bfinforLogLevel', '');
		localStorageInstance?.setItem('bfinforLogLevelOverride', '');
	}

	/**
	 * Can be called from console.
	 * Sets the root loglevel, or a specific logging level for a given logger
	 *
	 * example for setting root level:
	 * log.setLevel("warn")
	 *
	 * example for setting level for named logger:
	 * log.setLevel("UpdateProvider", "warn")
	 *
	 * @param levelStr
	 * @param name
	 */
	static setLevel(levelStr: string, name?: string): void {
		levelStr = levelStr.toUpperCase();

		if (!(levelStr in LogLevel)) {
			throw new Error(`Invalid level: ${levelStr}`);
		}

		const level = LogLevel[levelStr];

		if (name) {
			// set override for given logger name and save it to localStorage
			Logger._overrideLevel.set(name, level.id);
			const mapEntries = Logger._overrideLevel.entries();
			localStorageInstance?.setItem('bfinforLogLevelOverride', JSON.stringify([...mapEntries]));
		} else {
			Logger._rootLevel = level;
			localStorageInstance?.setItem('bfinforLogLevel', levelStr);
		}
	}

	// ---------------------------------------------------------
	// Logger instance properties

	// window is not defined during server side rendering
	logger = typeof window !== 'undefined' ? window.console : undefined;
	name = '';

	constructor(name: string) {
		this.name = name;
	}

	/**
	 * Returns the current level for this logger
	 *
	 * If no level is configured for this specific logger,
	 * default to the root level.
	 */
	get level() {
		if (Logger._overrideLevel.has(this.name)) {
			return LogLevel.fromId(Logger._overrideLevel.get(this.name));
		}
		return Logger._rootLevel;
	}

	log(level, loggerSubName?, ...args) {
		if (this.level.id < level.id) {
			return;
		}

		// (must convert to logger method on console object)
		// debug/info/warn/error
		const loggerFunctionName = level.enumKey.toLowerCase();

		// first argument may be reference to a function or method
		// in that case, add ()
		if (typeof loggerSubName == 'function') {
			loggerSubName = loggerSubName.name ? loggerSubName.name + '()' : '';
		}

		this.logger?.[loggerFunctionName]?.call(this.logger, `${level.enumKey} [${this.name}]`, loggerSubName, ...args);
	}

	debug(...args) {
		this.log(LogLevel.DEBUG, ...args);
	}

	info(...args) {
		this.log(LogLevel.INFO, ...args);
	}

	warn(...args) {
		this.log(LogLevel.WARN, ...args);
	}

	error(...args) {
		this.log(LogLevel.ERROR, ...args);
	}
}
